import React, { useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import globalCourseStore from '../../../stores/global-course-store';
import globalProgramStore from '../../../stores/program-store';
import { CourseFeedbackPoll } from '../../../api/types/programs/course/feedback/course-feedback-poll';
import { CoursePollWithVotesResponse } from '../../../api/types/students/course-poll-feedbacks/course-poll-with-questions';
import { AddFeedbackDialog } from './dialogs/add-feedback-dialog';
import { DeleteFeedbackDialog } from './dialogs/delete-feedback-dialog';
import { EmptyFeedbackState } from './components/empty-feedback-state';
import { PollCard } from './components/poll-card/poll-card';

export interface CourseFeedbackTabProps {
  isProfessor?: boolean;
}

export const CourseFeedbackTab: React.FC<CourseFeedbackTabProps> = observer(
  ({ isProfessor = false }) => {
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [deleteFeedback, setDeleteFeedback] =
      useState<CourseFeedbackPoll | null>(null);
    const [expandedPollIds, setExpandedPollIds] = useState<number[]>([]);

    const programId = globalProgramStore.getCurrentId();
    const courseId = globalCourseStore.getCurrentCourse()?.metadata?.id;
    const courseDetails = globalCourseStore.getCurrentCourse()?.details;

    // Get polls from course details
    const polls: CoursePollWithVotesResponse[] = useMemo(() => {
      return courseDetails?.polls || [];
    }, [courseDetails]);

    const toggleExpand = (pollId: number) => {
      setExpandedPollIds((prev) =>
        prev.includes(pollId)
          ? prev.filter((id) => id !== pollId)
          : [...prev, pollId],
      );
    };

    if (!programId || !courseId) {
      return null;
    }

    return (
      <div style={{ direction: 'rtl' }}>
        {/* Add Feedback Dialog */}
        <Dialog
          header="הוספת משוב הוראה"
          visible={showAddDialog}
          onHide={() => setShowAddDialog(false)}
          style={{
            width: '500px',
            backgroundColor: 'white',
            padding: '8px',
            borderRadius: '8px',
          }}
        >
          <AddFeedbackDialog
            programId={programId}
            courseId={courseId}
            closeDialog={() => setShowAddDialog(false)}
          />
        </Dialog>
        {/* Delete Feedback Dialog */}
        <Dialog
          header="מחיקת משוב"
          visible={deleteFeedback !== null}
          onHide={() => setDeleteFeedback(null)}
          style={{
            width: '450px',
            backgroundColor: 'white',
            padding: '8px',
            borderRadius: '8px',
          }}
        >
          {deleteFeedback && (
            <DeleteFeedbackDialog
              programId={programId}
              courseId={courseId}
              feedback={deleteFeedback}
              closeDialog={() => setDeleteFeedback(null)}
            />
          )}
        </Dialog>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <div>
            <h3 style={{ margin: '0 0 4px 0', color: '#1f2937' }}>
              משובי הוראה
            </h3>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
              ניהול משובים למרצים בקורס זה
            </p>
          </div>
          <Button
            label="הוסף משוב"
            icon="pi pi-plus"
            onClick={() => setShowAddDialog(true)}
          />
        </div>
        {/* Polls List */}
        {polls.length === 0 ? (
          <EmptyFeedbackState onAddFeedback={() => setShowAddDialog(true)} />
        ) : (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
          >
            {polls.map((poll) => (
              <PollCard
                key={poll.id}
                poll={poll}
                isExpanded={expandedPollIds.includes(poll.id)}
                onToggleExpand={() => toggleExpand(poll.id)}
                onDelete={setDeleteFeedback}
              />
            ))}
          </div>
        )}
      </div>
    );
  },
);
